import {Component, HostListener, ViewEncapsulation} from '@angular/core';
import { IFilterAngularComp } from 'ag-grid-angular';
import { IDoesFilterPassParams, IFilterParams } from 'ag-grid-community';

interface DateFilterCondition {
  type: string;        // equals, lessThan, ...
  valueString: string; // yyyy-MM-dd from <input type="date">
}

@Component({
  selector: 'app-custom-date-filter',
  templateUrl: './custom-date-filter.component.html',
  styleUrls: ['./custom-date-filter.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CustomDateFilterComponent implements IFilterAngularComp {
  private params!: IFilterParams;

  public filterConditions: DateFilterCondition[] = [
    { type: 'equals', valueString: '' },
    { type: 'equals', valueString: '' }
  ];

  public junction: 'AND' | 'OR' = 'AND';
  public dropdownOpenIndex: number | null = null;

  filterOptions = [
    { value: 'equals',      label: 'Equals' },
    { value: 'notEqual',    label: 'Does not equal' },
    { value: 'lessThan',    label: 'Before' },
    { value: 'greaterThan', label: 'After' },
    { value: 'inRange',     label: 'Between' },
    { value: 'blank',       label: 'Blank' },
    { value: 'notBlank',    label: 'Not blank' }
  ];

  agInit(params: IFilterParams): void {
    this.params = params;
  }

  // -------- helpers --------
  isBlankType(type: string): boolean {
    return type === 'blank' || type === 'notBlank';
  }

  isFirstConditionSet(): boolean {
    const c = this.filterConditions[0];
    return this.isBlankType(c.type) || !!c.valueString;
  }

  private parseDate(str: string): Date | null {
    if (!str) return null;
    const isoDateMatch = /^\d{4}-\d{2}-\d{2}$/.test(str);
    if (isoDateMatch) {
      const [yearStr, monthStr, dayStr] = str.split('-');
      const year = Number(yearStr);
      const month = Number(monthStr); // 1-12
      const day = Number(dayStr);
      const d = new Date(year, month - 1, day);
      if (
        !Number.isNaN(d.getTime()) &&
        d.getFullYear() === year &&
        d.getMonth() === month - 1 &&
        d.getDate() === day
      ) {
        return d;
      }
      return null;
    }
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private getCellDate(params: IDoesFilterPassParams): Date | null {
  const field = this.params?.colDef?.field;
  if (!field) return null;

  let value: any = params.data;
  // support nested field like "modDate.$date"
  for (const key of field.split('.')) {
    value = value ? value[key] : null;
  }

  if (value == null) return null;

  // handle mongodb-style {$date: '...'} or direct ISO string/number
  if (typeof value === 'object' && value.$date) {
    value = value.$date;
  }

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

  private normalize(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  // -------- AG callbacks --------
  isFilterActive(): boolean {
    return this.filterConditions.some(
      c => this.isBlankType(c.type) || !!c.valueString
    );
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    const cellDate = this.getCellDate(params);
    const c1 = this.filterConditions[0];
    const c2 = this.filterConditions[1];

    // inRange uses 2 dates
    if (c1.type === 'inRange') {
      const from = this.parseDate(c1.valueString);
      const to = this.parseDate(c2?.valueString);
      if (!from || !to) return true; // incomplete => don't filter out
      if (!cellDate) return false;
      const t = this.normalize(cellDate).getTime();
      return t >= this.normalize(from).getTime() &&
             t <= this.normalize(to).getTime();
    }

    const pass1 = this.checkCondition(c1, cellDate);

    if (!this.isFirstConditionSet() ||
        (!c2 || (!this.isBlankType(c2.type) && !c2.valueString))) {
      return pass1;
    }

    const pass2 = this.checkCondition(c2, cellDate);
    return this.junction === 'AND' ? pass1 && pass2 : pass1 || pass2;
  }

  private checkCondition(cond: DateFilterCondition, cellDate: Date | null): boolean {
    const filterDate = this.parseDate(cond.valueString);

    switch (cond.type) {
      case 'equals':
        if (!cellDate || !filterDate) return false;
        return this.normalize(cellDate).getTime() === this.normalize(filterDate).getTime();

      case 'notEqual':
        if (!cellDate || !filterDate) return false;
        return this.normalize(cellDate).getTime() !== this.normalize(filterDate).getTime();

      case 'lessThan':
        if (!cellDate || !filterDate) return false;
        return this.normalize(cellDate).getTime() < this.normalize(filterDate).getTime();

      case 'greaterThan':
        if (!cellDate || !filterDate) return false;
        return this.normalize(cellDate).getTime() > this.normalize(filterDate).getTime();

      case 'blank':
        return !cellDate;

      case 'notBlank':
        return !!cellDate;

      case 'inRange':
        return true;

      default:
        return true;
    }
  }

  getModel(): any {
    return {
      conditions: this.filterConditions,
      junction: this.junction
    };
  }

  setModel(model: any): void {
    if (!model) {
      this.resetFilter();
      return;
    }
    if (model.conditions) {
      this.filterConditions = model.conditions;
    }
    if (model.junction) {
      this.junction = model.junction;
    }
  }

  resetFilter(): void {
    this.filterConditions = [
      { type: 'equals', valueString: '' },
      { type: 'equals', valueString: '' }
    ];
    this.junction = 'AND';
    this.onFilterChanged();
  }

  onFilterChanged(): void {
    this.params.filterChangedCallback();
  }

  // -------- dropdown behaviour  --------
  getOptionLabel(value: string): string {
    const match = this.filterOptions.find(o => o.value === value);
    return match ? match.label : this.filterOptions[0].label;
  }

  toggleDropdown(index: number): void {
    this.dropdownOpenIndex = this.dropdownOpenIndex === index ? null : index;
  }

  selectOption(index: number, optionValue: string): void {
    this.filterConditions[index].type = optionValue;
    this.onFilterChanged();
    this.dropdownOpenIndex = null;
  }


  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.select-wrapper')) {
      this.dropdownOpenIndex = null;
    }
  }
}
